import {
	ApplePayButton,
	ButtonType,
	Edges,
	Environment,
	Locale,
	Scope,
	SupportedNetworks,
	ThemeMode
} from '@tap-payments/apple-pay-button';

import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Skeleton from 'components/global/Skeleton';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';

import axios from 'services/axios';
import { OTO_LINK } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';
import { PAYMENT_GATEWAY_TYPE } from 'utils/constants';

import * as S from './Tap.styled';

let popup;

const ChargeApplePay = ({
	onComplete,
	body = {},
	paymentGatewayType: paymentGatewayTypeProp,
	setApplePayReady,
	isKYCPayment
}) => {
	const formMethods = useFormContext();
	const [isReady, setIsReady] = useState(false);
	const { paymentGatewayType } = useSelector((state) => state?.user.localization) || paymentGatewayTypeProp;
	const { LangCountryCode } = useLocale();
	const { darkMode } = useSelector((state) => state?.layout);
	const { showError } = useAlert();
	const dispatch = useDispatch();
	const { watch } = formMethods;

	const checkPayment = async (id) =>
		axios
			.post(OTO_LINK.CHECK_CHARGE, {
				ID: id,
				key: watch('key'),
				GUID: watch('GUID'),
				otoLinkSessionId: watch('otoLinkSessionId')
			})
			.then((res) => {
				const { data, status } = res;
				if (status === 200 && data) {
					if (data.status === 'failed') {
						showError({
							header: 'error',
							...(data?.description && { message: data?.description })
						});
						AppLogger.error(data);
						dispatch(setLoading(false));
					} else if (data.status === 'paid') {
						dispatch(setLoading(false));
						onComplete(data);
					} else {
						setTimeout(() => {
							checkPayment(id);
						}, 3 * 1000);
					}
				} else {
					showError();
					AppLogger.error(res);
					dispatch(setLoading(false));
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
				dispatch(setLoading(false));
			});

	useEffect(() => {
		const handleMessage = (message) => {
			if (message.data.event !== 'checkPaymentResult') return;

			popup.close();

			setTimeout(() => {
				checkPayment(message.data?.paymentID);
			}, 5 * 1000); // wait for 5 second to make sure the webhook is triggered
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, []);

	const completeCreditPayment = async (token) => {
		dispatch(setLoading(true));

		await axios
			.post(OTO_LINK.MAKE_CHARGE, {
				...body,
				token
			})
			.then(async (res) => {
				const { data, status } = res;
				if (status === 200 && data) {
					checkPayment(data?.paymentID);
				} else {
					showError();
					AppLogger.error(res);
					dispatch(setLoading(false));
				}
			})
			.catch((error) => {
				dispatch(setLoading(false));
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});

				AppLogger.error(error);
			});
	};

	return (
		<>
			{!isReady && (
				<S.ApplePayWrapper>
					<Skeleton width="12rem" height="6rem" />
				</S.ApplePayWrapper>
			)}
			<S.ApplePayWrapper
				css={{
					...(!isReady && { visibility: 'hidden' })
				}}>
				<ApplePayButton
					// The public Key provided by Tap
					publicKey={
						paymentGatewayType === PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE
							? isKYCPayment
								? import.meta.env.VITE_TAP_PAYMENT_UAE_APPLE_PAY_KEY_KYC
								: import.meta.env.VITE_TAP_PAYMENT_UAE_APPLE_PAY_KEY
							: isKYCPayment
								? import.meta.env.VITE_TAP_PAYMENT_APPLE_PAY_KEY_KYC
								: import.meta.env.VITE_TAP_PAYMENT_APPLE_PAY_KEY
					}
					// The environment of the SDK and it can be one of these environments
					environment={
						import.meta.env.VITE_ENVIRONMENT === 'production' ||
						import.meta.env.VITE_ENVIRONMENT === 'prerelease'
							? Environment.Production
							: Environment.Development
					}
					merchant={{
						//  The merchant domain name
						domain: 'app.tryoto.com',
						//  The merchant identifier provided by Tap
						id:
							paymentGatewayType === PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE
								? import.meta.env.VITE_TAP_PAYMENT_UAE_MERCHANT_ID
								: import.meta.env.VITE_TAP_PAYMENT_MERCHANT_ID
					}}
					features={{
						supportsCouponCode: false
					}}
					transaction={{
						// The amount to be charged
						amount: body?.amount,
						// The currency of the amount
						currency: watch('currency')
					}}
					// The scope of the SDK and it can be one of these scopes:
					// [TapToken,AppleToken], by default it is TapToken)
					scope={Scope.TapToken}
					acceptance={{
						// The supported networks for the Apple Pay button and it
						// can be one of these networks: [Mada,Visa,MasterCard], by default
						// we bring all the supported networks from tap merchant configuration
						supportedBrands:
							paymentGatewayType === PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE
								? [SupportedNetworks.Visa, SupportedNetworks.MasterCard]
								: [SupportedNetworks.Mada, SupportedNetworks.Visa, SupportedNetworks.MasterCard]
					}}
					// The billing contact information
					customer={{
						name: [
							{
								// "en or ar",
								lang: LangCountryCode?.split('_').shift() === 'en' ? Locale.EN : Locale.AR,
								// "First name of the customer.",
								first: watch('firstName'),
								// "Last name of the customer.",
								last: watch('lastName')
							}
						],
						// Defines the contact details for the customer & to be used in creating the billing contact info in Apple pay request
						contact: {
							// "The customer's email",
							email: watch('email'),
							// "The customer's phone number"
							phone: {
								// "The customer's country code",
								countryCode: body?.countryCode,
								// "The customer's phone number
								number: body?.phone
							}
						}
					}}
					// for styling button
					interface={{
						// The locale of the Apple Pay button and it can be one of these locales:[EN,AR]
						locale: LangCountryCode?.split('_').shift() === 'en' ? Locale.EN : Locale.AR,
						// The theme of the Apple Pay button and it can be one of
						// these values : [light,Dark], by default it is detected from user device
						theme: darkMode ? ThemeMode.LIGHT : ThemeMode.DARK,
						// The type of the Apple Pay
						type: ButtonType.PAY,
						// The border of the Apple Pay button and it can be one of these values:[curved,straight]
						edges: Edges.CURVED
					}}
					// optional (A callback function that will be called when you cancel
					// the payment process)
					onCancel={() => {
						AppLogger.log('Apple Pay payment process has been canceled by the user.');
					}}
					// optional (A callback function that will be called when you have an error)
					onError={(err) => {
						AppLogger.error(err);
						showError();
					}}
					onClick={() => {
						AppLogger.log('Apple Pay button is clicked');
					}}
					// optional (A async function that will be called after creating the token
					// successfully)
					onSuccess={async (token) => {
						// do your stuff here...
						AppLogger.log(token);
						completeCreditPayment(token);
					}}
					onReady={() => {
						setApplePayReady?.(true);
						setIsReady(true);
						AppLogger.log('Apple Pay button is ready');
					}}
				/>
			</S.ApplePayWrapper>
		</>
	);
};

export default ChargeApplePay;
